import Axios from '../config/axios-config';

const BASE = 'delivery-quality/scores';
const BASE_AI_PERF = 'delivery-quality/ai-performance';

const buildQs = (params = {}) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

const withQs = (url, params) => {
  const qs = buildQs(params);
  return qs ? `${url}?${qs}` : url;
};

export const deliveryQualityService = {
  // Listado de evaluaciones individuales, ordenado por fecha desc (las últimas
  // primero). Acepta { logistic_company_id|company_id, limit, page, date_from,
  // date_to, deliverer_id, ... }. Devuelve { data: ScoreItem[], total, ... }.
  getRecentScores: (params) => Axios.get(withQs(BASE, params)),
  getGlobal: (params) => Axios.get(withQs(`${BASE}/aggregated/global`, params)),
  getByMunicipality: (params) => Axios.get(withQs(`${BASE}/aggregated/by-municipality`, params)),
  getByDriver: (params) => Axios.get(withQs(`${BASE}/aggregated/by-driver`, params)),
  getByType: (params) => Axios.get(withQs(`${BASE}/aggregated/by-type`, params)),
  getByCompany: (params) => Axios.get(withQs(`${BASE}/aggregated/by-company`, params)),
  getTimeline: (params) => Axios.get(withQs(`${BASE}/timeline`, params)),
  // Embudo de calidad: Entregados -> Evaluados -> Con IA -> OK / Dudosa / Rechazada
  // para un mismo (date_from, date_to, company_id|logistic_company_id).
  getCoverage: (params) => Axios.get(withQs(`${BASE}/coverage`, params)),
  getDriverBreakdown: (driverId, params = {}) => {
    // Compatibilidad hacia atrás: si llaman pasando un número, lo tratamos como days.
    const qs = typeof params === 'number' ? { days: params } : { days: 30, ...params };
    return Axios.get(withQs(`${BASE}/driver/${driverId}/breakdown`, qs));
  },
  getDriverHistory: (driverId, params = {}) =>
    Axios.get(withQs(`${BASE}/driver/${driverId}`, params)),
  getScoreByPackage: (packageId) => Axios.get(`${BASE}/${packageId}`),
  resolveAppeal: ({ packageId, resolution, note, aspects }) =>
    Axios.post(`${BASE}/${packageId}/appeal/resolve`, { resolution, note, aspects }).then((r) => r.data),

  // ── AI Performance (proxy server-side a calidad-ia /v1/metrics v2) ──
  // Acepta { window, from, to, company_id, logistic_company_id,
  //          time_dimension, source }.
  //
  //  - from/to: rango absoluto ISO; si se pasa uno, ambos son obligatorios.
  //    Window se ignora cuando vienen ambos.
  //  - company_id: filtra por empresa duena del paquete. El upstream valida
  //    y devuelve counts en cero si no hay match.
  //  - logistic_company_id: filtra por operador logistico. Se usa con
  //    cuentas tipo `logistic`, donde el usuario ve paquetes de N empresas
  //    distintas y company_id no es discriminante.
  //  - time_dimension: finished_at | created_at | delivery_date.
  //  - source: live | batch | backfill | manual | reconciler. Sin valor =
  //    todos los buckets.
  //  Ver delivery-quality/docs/calidad-ia-metrics-coordination.md.
  getAiPerformanceMetrics: ({
    window,
    from,
    to,
    company_id,
    logistic_company_id,
    time_dimension,
    source,
  } = {}) =>
    Axios.get(
      withQs(`${BASE_AI_PERF}/metrics`, {
        window,
        from,
        to,
        company_id,
        logistic_company_id,
        time_dimension,
        source,
      }),
    ),
};

// Named exports (mismo contrato que los métodos del objeto: getScoreByPackage
// devuelve la respuesta cruda de Axios — usa `.then(r => r.data)`).
export const getScoreByPackage = deliveryQualityService.getScoreByPackage;
export const resolveAppeal = ({ packageId, resolution, note, aspects }) =>
  Axios.post(`delivery-quality/scores/${packageId}/appeal/resolve`, { resolution, note, aspects }).then((r) => r.data);

export default deliveryQualityService;
